import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Btn,
  Card,
  CardBody,
  Chip,
  LoadingSpinner,
  MultilineText,
} from '@/presentation/component/ui';
import { useCatalog } from '@/presentation/hook/useCatalog';
import { useAuth } from '@/presentation/hook/useAuth';
import { hasSubcategoryAccess } from '@/domain/model/accessUtils';
import { formatCFA } from '@/presentation/util/format';

export const CategoryViewPage = () => {
  const { t } = useTranslation();
  const { categoryId } = useParams<{ categoryId: string }>();
  const { currentCategory, isLoading, fetchCategory, clearCurrentCategory } = useCatalog();
  const { user, isAuthenticated, isAdmin } = useAuth();

  useEffect(() => {
    if (categoryId) {
      fetchCategory(categoryId);
    }
    return () => clearCurrentCategory();
  }, [categoryId, fetchCategory, clearCurrentCategory]);

  const hasAccessTo = (subcategoryId: string) =>
    isAuthenticated && hasSubcategoryAccess(user, isAdmin, subcategoryId);

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!currentCategory) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[var(--color-ink)] mb-4">
            {t('category.categoryNotFound')}
          </h1>
          <Link to="/formation">
            <Btn kind="primary">{t('buttons.backToFormations')}</Btn>
          </Link>
        </div>
      </div>
    );
  }

  const subcategories = currentCategory.subcategories ?? [];

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <Link
                to="/formation"
                className="text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]"
              >
                {t('nav.formations')}
              </Link>
            </li>
            <li className="text-[var(--color-ink-faint)]">/</li>
            <li className="text-[var(--color-ink)]">{currentCategory.name}</li>
          </ol>
        </nav>

        <div className="mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-ink)] mb-4">
            {currentCategory.name}
          </h1>
          <MultilineText className="text-lg sm:text-xl text-[var(--color-ink-soft)]">
            {currentCategory.description}
          </MultilineText>
        </div>

        {subcategories.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-[var(--color-ink)] mb-6">
              {t('category.availableFormations')}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {subcategories.map((subcategory) => {
                const owned = hasAccessTo(subcategory.id);

                return (
                  <Card key={subcategory.id} className="flex flex-col">
                    <CardBody className="flex flex-col flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-xl font-semibold text-[var(--color-ink)]">
                          {subcategory.name}
                        </h3>
                        {owned && <Chip kind="good">{t('category.accessGranted')}</Chip>}
                      </div>
                      <MultilineText className="text-[var(--color-ink-soft)] mb-4 line-clamp-3">
                        {subcategory.description}
                      </MultilineText>

                      <div className="flex items-center justify-end mb-4 mt-auto">
                        <span className="text-lg font-bold text-[var(--color-accent)]">
                          {formatCFA(subcategory.price)}
                        </span>
                      </div>

                      <Link to={`/subcategory/${subcategory.id}`}>
                        <Btn kind={owned ? 'primary' : 'default'} className="w-full" size="sm">
                          {owned ? t('buttons.startLearning') : t('buttons.viewDetails')}
                        </Btn>
                      </Link>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {subcategories.length === 0 && (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <h3 className="text-lg font-semibold text-[var(--color-ink)] mb-2">
                {t('category.noSubcategories')}
              </h3>
              <p className="text-[var(--color-ink-faint)]">{t('category.comingSoonMessage')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
